/**
 * Player figures timezone = the viewer's local timezone.
 * Calendar days / MonΓÇôSun weeks roll at local midnight.
 * Admin weekly figures use a separate fixed Pacific clock (see adminFiguresTime).
 */
export const getFiguresTimeZone = (): string =>
  Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

/** @deprecated Prefer getFiguresTimeZone(); kept for call sites that read a string. */
export const FIGURES_TIMEZONE = getFiguresTimeZone();

const WEEKDAY_TO_MONDAY_INDEX: Record<string, number> = {
  Mon: 0,
  Tue: 1,
  Wed: 2,
  Thu: 3,
  Fri: 4,
  Sat: 5,
  Sun: 6,
};

export type FiguresZonedParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
  weekday: string;
};

const getZonedParts = (date: Date, timeZone = getFiguresTimeZone()): FiguresZonedParts => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    weekday: "short",
    hourCycle: "h23",
  }).formatToParts(date);

  const map: Record<string, string> = {};
  for (const part of parts) {
    if (part.type !== "literal") map[part.type] = part.value;
  }

  return {
    year: Number(map.year),
    month: Number(map.month),
    day: Number(map.day),
    hour: Number(map.hour),
    minute: Number(map.minute),
    second: Number(map.second),
    weekday: map.weekday,
  };
};

/** Offset such that truncatedDate.getTime() === wallAsUtc - offset (second precision). */
const getTimeZoneOffsetMs = (date: Date, timeZone = getFiguresTimeZone()): number => {
  const parts = getZonedParts(date, timeZone);
  const wallAsUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second
  );
  const truncated = Math.floor(date.getTime() / 1000) * 1000;
  return wallAsUtc - truncated;
};

/** Convert a wall-clock time in the player's local TZ into an absolute Date. */
export const figuresWallTimeToUtc = (
  year: number,
  month: number,
  day: number,
  hour = 0,
  minute = 0,
  second = 0,
  ms = 0
): Date => {
  let utc = Date.UTC(year, month - 1, day, hour, minute, second, 0);
  for (let i = 0; i < 2; i++) {
    const offset = getTimeZoneOffsetMs(new Date(utc));
    utc = Date.UTC(year, month - 1, day, hour, minute, second, 0) - offset;
  }
  return new Date(utc + ms);
};

export const getFiguresZonedParts = (date: Date): FiguresZonedParts =>
  getZonedParts(date);

/** Monday = 0 ΓÇª Sunday = 6 in the player's local timezone. */
export const getFiguresWeekdayIndex = (date: Date): number => {
  const { weekday } = getZonedParts(date);
  return WEEKDAY_TO_MONDAY_INDEX[weekday] ?? 0;
};

export const getFiguresDayStart = (date: Date): Date => {
  const { year, month, day } = getZonedParts(date);
  return figuresWallTimeToUtc(year, month, day, 0, 0, 0, 0);
};

export const getFiguresDayEnd = (date: Date): Date => {
  const nextStart = addFiguresCalendarDays(getFiguresDayStart(date), 1);
  return new Date(nextStart.getTime() - 1);
};

export const addFiguresCalendarDays = (date: Date, days: number): Date => {
  const { year, month, day } = getZonedParts(date);
  // Noon avoids DST edge cases when shifting calendar days.
  const mid = figuresWallTimeToUtc(year, month, day, 12, 0, 0, 0);
  mid.setTime(mid.getTime() + days * 24 * 60 * 60 * 1000);
  return getFiguresDayStart(mid);
};

export const isSameFiguresCalendarDay = (a: Date, b: Date): boolean => {
  const pa = getZonedParts(a);
  const pb = getZonedParts(b);
  return pa.year === pb.year && pa.month === pb.month && pa.day === pb.day;
};

/** Stable day key for grouping (player local calendar date). */
export const getFiguresDateKey = (date: Date): string => {
  const { year, month, day } = getZonedParts(date);
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
};

export const formatFiguresDateLabel = (
  date: Date,
  options: Intl.DateTimeFormatOptions
): string =>
  date.toLocaleDateString("en-US", { ...options, timeZone: getFiguresTimeZone() });

export const formatFiguresDateTime = (
  date: Date,
  options?: Intl.DateTimeFormatOptions,
): string =>
  date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "numeric",
    hour12: true,
    ...options,
    timeZone: getFiguresTimeZone(),
  });

/** First day of the local month containing `date`, shifted by `monthsAgo`. */
export const getFiguresMonthStart = (date: Date = new Date(), monthsAgo = 0): Date => {
  const parts = getZonedParts(date);
  const totalMonths = parts.year * 12 + (parts.month - 1) - monthsAgo;
  const year = Math.floor(totalMonths / 12);
  const month = (totalMonths % 12) + 1;
  return figuresWallTimeToUtc(year, month, 1, 0, 0, 0, 0);
};

export const getFiguresMonthKey = (date: Date): string => {
  const { year, month } = getZonedParts(date);
  return `${year}-${String(month).padStart(2, "0")}`;
};

export const getFiguresYearKey = (date: Date): string => String(getZonedParts(date).year);

/** Stable MonΓÇôSun week key = Monday's local date key. */
export const getFiguresWeekKey = (date: Date): string => {
  const dayStart = getFiguresDayStart(date);
  const monday = addFiguresCalendarDays(dayStart, -getFiguresWeekdayIndex(dayStart));
  return getFiguresDateKey(monday);
};
/**
 * ISO week id for weeklyFigures.weekId: "YYYY-WW" (e.g. "2026-30").
 * Matches weeklyFigures docs written by the backend.
 */
export const getFiguresIsoWeekId = (date: Date): string => {
  const dayStart = getFiguresDayStart(date);
  const monday = addFiguresCalendarDays(dayStart, -getFiguresWeekdayIndex(dayStart));
  const thursday = addFiguresCalendarDays(monday, 3);
  const { year: isoYear } = getZonedParts(thursday);
  const jan4 = figuresWallTimeToUtc(isoYear, 1, 4, 12, 0, 0, 0);
  const week1Monday = addFiguresCalendarDays(
    getFiguresDayStart(jan4),
    -getFiguresWeekdayIndex(jan4)
  );
  const weekNum =
    Math.round((monday.getTime() - week1Monday.getTime()) / (7 * 24 * 60 * 60 * 1000)) + 1;
  return `${isoYear}-${String(weekNum).padStart(2, "0")}`;
};
